import { getInvoicesList, getPaymentsList } from 'actions/billing'
import moment from 'moment'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

import { selectIsInternalAdmin } from '../../accessors'
import ConfirmationDialog from '../../components/ReusableComponents/ConfirmationDialog'
import { useManagePaymentPermissions } from '../../hooks/useManagePaymentPermissions'
import billingApi from '../../services/api/billing'
import { delay } from '../../utils/delay'
import BillingRow from './BillingRow/index'
import {
  canAddChargeback,
  canDoRefund,
  canRecordRefund,
  getChargebackWindowClosedReason,
  getRefundWindowClosedReason,
  isRefundPending
} from './paymentRefundHelpers'
import styles from './PaymentRow.module.scss'

const Button = ({ children, handleClick }) => (
  <button
    onClick={handleClick}
    style={{
      padding: 0,
      border: 0,
      fontSize: 12,
      color: '#2764EA',
      marginTop: 2,
      marginLeft: 5,
      display: 'block',
      textDecoration: 'underline'
    }}
  >
    {children}
  </button>
)

export const PaymentRow = props => {
  const {
    paymentClearedOn,
    paymentAddedOn,
    paymentMethod,
    paymentAmount,
    paymentFriendlyName,
    paidForSubsidiary,
    paymentProcessedBy,
    processorStatus,
    handleRetry,
    paymentConfirmationCode,
    handleMarkAsPaid,
    handleMarkAsRejected,
    paymentPostedBy,
    paymentNotes,
    id,
    chargebackDate,
    chargebackBy,
    hasChargeback,
    hasRefund,
    refundDate,
    refundRequestedOn,
    refundBy,
    refundReason,
    statusChangedOn,
    statusUpdateReason,
    achReturnCode
  } = props

  const dispatch = useDispatch()
  const { addToast } = useToasts()

  const niceMethodNames = {
    CreditCard: 'Credit card',
    Check: 'Check',
    ACH: 'ACH'
  }

  const niceStatusNames = {
    InitialApproval: 'In process',
    Success: 'Cleared on',
    Error: 'Failed',
    Declined: 'Declined',
    ChargedBack: 'Charged back on'
  }

  const statusColors = {
    Declined: 'bad',
    InitialApproval: 'none',
    Success: 'good',
    Error: 'bad',
    ChargedBack: 'bad'
  }

  let title

  if (paymentProcessedBy === 'CreditIssued') {
    title = `Hotel credit for ${paidForSubsidiary}`
  } else if (paymentProcessedBy === 'CreditIssuedFromACH') {
    title = 'Credit applied for incoming ACH'
  } else if (paymentNotes === 'SingleUseCreditCard') {
    title = 'Single use credit card'
  } else {
    const displayPaymentMethod =
      paymentProcessedBy === 'BankDirect'
        ? 'Check'
        : niceMethodNames[paymentMethod]
        ? niceMethodNames[paymentMethod]
        : paymentMethod

    title = `${displayPaymentMethod} for ${paidForSubsidiary}`
  }

  const processDate =
    processorStatus === 'Success' ? moment(paymentClearedOn).format('MMM DD') : ''

  const [showingMarkAsPaidConfirmation, setShowingMarkAsPaidConfirmation] = useState()

  const [showingMarkAsRejectedConfirmation, setShowingMarkAsRejectedConfirmation] =
    useState()

  const [refundAction, setRefundAction] = useState()
  const [showingChargebackConfirmation, setShowingChargebackConfirmation] =
    useState(false)
  const [refundNotes, setRefundNotes] = useState('')
  const [chargebackNotes, setChargebackNotes] = useState('')
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false)
  const [isSubmittingChargeback, setIsSubmittingChargeback] = useState(false)

  function handleConfirmMarkAsPaid() {
    setShowingMarkAsPaidConfirmation(false)
    handleMarkAsPaid()
  }

  function handleConfirmMarkAsRejected() {
    setShowingMarkAsRejectedConfirmation(false)
    handleMarkAsRejected()
  }

  function refreshBillingLists() {
    delay(1000).then(() => {
      dispatch(getInvoicesList())
      dispatch(getPaymentsList())
    })
  }

  function handleConfirmRefund() {
    if (isSubmittingRefund) {
      return
    }

    setIsSubmittingRefund(true)
    const refundRequest =
      refundAction === 'process'
        ? billingApi.doPaymentRefund
        : billingApi.recordPaymentRefund

    refundRequest({ paymentId: id, refundReason: refundNotes })
      .then(response => {
        const status = response?.refundProcessorStatus
        if (status === 'Pending') {
          addToast('Refund submitted and is pending processor approval.', {
            appearance: 'info'
          })
        } else if (refundAction === 'process') {
          addToast('Nuvei refund completed.', { appearance: 'success' })
        } else {
          addToast('Refund recorded.', { appearance: 'success' })
        }
        refreshBillingLists()
      })
      .catch(error => {
        addToast(`${error.message.toString()}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsSubmittingRefund(false)
        setRefundAction()
        setRefundNotes('')
      })
  }

  function handleConfirmChargeback() {
    if (isSubmittingChargeback) {
      return
    }

    setIsSubmittingChargeback(true)
    billingApi
      .setPaymentAsChargeback({
        paymentId: id,
        chargebackReason: chargebackNotes || 'Chargeback'
      })
      .then(() => {
        addToast('Chargeback recorded.', { appearance: 'success' })
        refreshBillingLists()
      })
      .catch(error => {
        addToast(`${error.message.toString()}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsSubmittingChargeback(false)
        setShowingChargebackConfirmation(false)
        setChargebackNotes('')
      })
  }

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const { canManagePaymentStatuses } = useManagePaymentPermissions()
  const paymentPendingRefund = isRefundPending(props)
  const showRecordRefund = canRecordRefund(props, canManagePaymentStatuses)
  const showDoRefund = canDoRefund(props, canManagePaymentStatuses)
  const showAddChargeback = canAddChargeback(props, canManagePaymentStatuses)
  const refundWindowClosedReason = getRefundWindowClosedReason(
    props,
    canManagePaymentStatuses
  )
  const chargebackWindowClosedReason = getChargebackWindowClosedReason(
    props,
    canManagePaymentStatuses
  )
  const formattedAmount = paymentAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  return (
    <>
      {showingMarkAsPaidConfirmation && (
        <ConfirmationDialog
          title="Mark payment as paid"
          maxWidth={500}
          positiveMessage="Mark as paid"
          onReject={() => setShowingMarkAsPaidConfirmation(false)}
          onConfirm={handleConfirmMarkAsPaid}
          hasDangerousAction
        >
          <p>Double check following details before confirming your request.</p>
          <div style={{ marginTop: 10 }}>
            <p>
              <strong>Subsidiary:</strong> {paidForSubsidiary}
            </p>
            <p>
              <b>Approval code:</b> {paymentConfirmationCode}
            </p>
            <p>
              <b>Processed by:</b> {paymentProcessedBy}
            </p>
            <p>
              <b>Payment method:</b> {paymentMethod}
            </p>
          </div>
        </ConfirmationDialog>
      )}
      {showingMarkAsRejectedConfirmation && (
        <ConfirmationDialog
          title="Mark payment as declined"
          maxWidth={500}
          positiveMessage="Mark as declined"
          onReject={() => setShowingMarkAsRejectedConfirmation(false)}
          onConfirm={handleConfirmMarkAsRejected}
          hasDangerousAction
        >
          <p>Double check following details before confirming your request.</p>
          <div style={{ marginTop: 10 }}>
            <p>
              <strong>Subsidiary:</strong> {paidForSubsidiary}
            </p>
            <p>
              <b>Approval code:</b> {paymentConfirmationCode}
            </p>
            <p>
              <b>Processed by:</b> {paymentProcessedBy}
            </p>
            <p>
              <b>Payment method:</b> {paymentMethod}
            </p>
          </div>
        </ConfirmationDialog>
      )}
      {refundAction && (
        <ConfirmationDialog
          title={refundAction === 'process' ? 'Do Refund' : 'Record a Refund'}
          maxWidth={500}
          positiveMessage={refundAction === 'process' ? 'Do refund' : 'Record refund'}
          onReject={() => setRefundAction()}
          onConfirm={handleConfirmRefund}
          isButtonDisabled={isSubmittingRefund}
          hasDangerousAction
        >
          {refundAction === 'process' ? (
            <p>
              This action sends a real, full refund of {formattedAmount} to Nuvei for
              payment #{id}. The money will be returned through the payment processor.
            </p>
          ) : (
            <p>
              This action only records that payment #{id} was refunded outside Directful.
              It does not send money or contact Nuvei.
            </p>
          )}
          <p>
            This payment will no longer count as cleared and the hotel balance will be
            recalculated. No invoice will be created.
          </p>
          <p>
            Only confirm after verifying the payment. The request, notes, date, and your
            user account will be saved for audit history.
          </p>
          <div style={{ marginTop: 10 }}>
            <p>
              <strong>Subsidiary:</strong> {paidForSubsidiary}
            </p>
            <p>
              <b>Payment method:</b> {paymentMethod}
            </p>
            <p>
              <b>Amount:</b> {formattedAmount}
            </p>
            <label htmlFor={`refund-notes-${id}`}>Notes</label>
            <textarea
              id={`refund-notes-${id}`}
              value={refundNotes}
              onChange={event => setRefundNotes(event.target.value)}
              rows={3}
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
        </ConfirmationDialog>
      )}
      {showingChargebackConfirmation && (
        <ConfirmationDialog
          title="Add Chargeback"
          maxWidth={500}
          positiveMessage="Record chargeback"
          onReject={() => setShowingChargebackConfirmation(false)}
          onConfirm={handleConfirmChargeback}
          isButtonDisabled={isSubmittingChargeback}
          hasDangerousAction
        >
          <p>
            This marks payment #{id} as a chargeback. The payment will no longer count as
            cleared and no invoice will be created.
          </p>
          <div style={{ marginTop: 10 }}>
            <p>
              <strong>Subsidiary:</strong> {paidForSubsidiary}
            </p>
            <p>
              <b>Amount:</b> {formattedAmount}
            </p>
            <label htmlFor={`chargeback-notes-${id}`}>Notes</label>
            <textarea
              id={`chargeback-notes-${id}`}
              value={chargebackNotes}
              onChange={event => setChargebackNotes(event.target.value)}
              rows={3}
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
        </ConfirmationDialog>
      )}
      <div className={styles.rowHolder}>
        {isInternalAdmin && (
          <div className={styles.tooltip}>
            <div>
              <span className={styles.tooltipLabel}>Payment #</span>
              <span className={styles.tooltipValue}>{id}</span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Posted by</span>
              <span className={styles.tooltipValue}>{paymentPostedBy}</span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Added on</span>
              <span className={styles.tooltipValue}>
                {moment(paymentAddedOn).format('YYYY-MM-DD')}
              </span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Cleared on</span>
              <span className={styles.tooltipValue}>
                {moment(paymentClearedOn).format('YYYY-MM-DD')}
              </span>
            </div>
            <div>
              <span className={styles.tooltipLabel}>Conf #</span>
              <span className={styles.tooltipValue}>{paymentConfirmationCode}</span>
            </div>
            {statusChangedOn && (
              <div>
                <span className={styles.tooltipLabel}>Status changed on</span>
                <span className={styles.tooltipValue}>
                  {moment(statusChangedOn).format('YYYY-MM-DD HH:mm')}
                </span>
              </div>
            )}
            {statusUpdateReason && (
              <div>
                <span className={styles.tooltipLabel}>Status reason</span>
                <span className={styles.tooltipValue}>{statusUpdateReason}</span>
              </div>
            )}
            {achReturnCode && (
              <div>
                <span className={styles.tooltipLabel}>ACH return</span>
                <span className={styles.tooltipValue}>{achReturnCode}</span>
              </div>
            )}
            <div>
              <span className={styles.tooltipLabel}>Notes</span>
              <span className={styles.tooltipValue}>{paymentNotes}</span>
            </div>
            {hasChargeback && (
              <div>
                <span className={styles.tooltipLabel}>Chargeback on</span>
                <span className={styles.tooltipValue}>
                  {moment(chargebackDate).format('YYYY-MM-DD')}
                </span>
              </div>
            )}
            {hasChargeback && (
              <div>
                <span className={styles.tooltipLabel}>Chargeback by</span>
                <span className={styles.tooltipValue}>{chargebackBy}</span>
              </div>
            )}
            {(hasRefund || refundDate) && (
              <div>
                <span className={styles.tooltipLabel}>Refunded on</span>
                <span className={styles.tooltipValue}>
                  {moment(refundDate).format('YYYY-MM-DD')}
                </span>
              </div>
            )}
            {(hasRefund || refundDate) && (
              <div>
                <span className={styles.tooltipLabel}>Refunded by</span>
                <span className={styles.tooltipValue}>{refundBy}</span>
              </div>
            )}
            {(hasRefund || refundDate) && refundReason && (
              <div>
                <span className={styles.tooltipLabel}>Refund notes</span>
                <span className={styles.tooltipValue}>{refundReason}</span>
              </div>
            )}
            {paymentPendingRefund && refundRequestedOn && (
              <div>
                <span className={styles.tooltipLabel}>Refund requested on</span>
                <span className={styles.tooltipValue}>
                  {moment(refundRequestedOn).format('YYYY-MM-DD')}
                </span>
              </div>
            )}
            {paymentPendingRefund && refundBy && (
              <div>
                <span className={styles.tooltipLabel}>Refund requested by</span>
                <span className={styles.tooltipValue}>{refundBy}</span>
              </div>
            )}
            {paymentPendingRefund && refundReason && (
              <div>
                <span className={styles.tooltipLabel}>Refund notes</span>
                <span className={styles.tooltipValue}>{refundReason}</span>
              </div>
            )}
            {paymentPendingRefund && (
              <div>
                <span className={styles.tooltipLabel}>Refund</span>
                <span className={styles.tooltipValue}>Pending processor approval</span>
              </div>
            )}
            {(processorStatus === 'InitialApproval' || processorStatus === 'Success') &&
              canManagePaymentStatuses && (
                <div className={styles.tooltipActions}>
                  {processorStatus === 'InitialApproval' && (
                    <>
                      <button onClick={() => setShowingMarkAsPaidConfirmation(true)}>
                        Mark as paid
                      </button>
                      <button onClick={() => setShowingMarkAsRejectedConfirmation(true)}>
                        Mark as declined
                      </button>
                    </>
                  )}
                  {showAddChargeback && (
                    <button onClick={() => setShowingChargebackConfirmation(true)}>
                      Add Chargeback
                    </button>
                  )}
                  {showRecordRefund && (
                    <button
                      className={styles.refundAction}
                      onClick={() => setRefundAction('record')}
                    >
                      Record a Refund
                    </button>
                  )}
                  {showDoRefund && (
                    <button onClick={() => setRefundAction('process')}>Do Refund</button>
                  )}
                  {chargebackWindowClosedReason && (
                    <span className={styles.windowClosedReason}>
                      {chargebackWindowClosedReason}
                    </span>
                  )}
                  {refundWindowClosedReason && (
                    <span className={styles.windowClosedReason}>
                      {refundWindowClosedReason}
                    </span>
                  )}
                </div>
              )}
          </div>
        )}
        <BillingRow
          pretitle={moment(paymentAddedOn).format('MMM DD, YYYY')}
          title={title}
          titleInfo={paymentFriendlyName}
          amount={formattedAmount}
          hasHoverState={false}
          amountInfo={
            <div className={styles.statusContainer}>
              {hasChargeback ? (
                <span>Chargeback</span>
              ) : hasRefund ? (
                <span>Refunded</span>
              ) : paymentPendingRefund ? (
                <span>Refund pending</span>
              ) : (
                <span>
                  {niceStatusNames[processorStatus] || processorStatus} {processDate}
                </span>
              )}
              <div className={styles.retryButton}>
                <div>
                  {(processorStatus === 'Error' || processorStatus === 'Declined') &&
                    paymentMethod === 'CreditCard' && (
                      <Button handleClick={handleRetry}>Retry</Button>
                    )}
                </div>
              </div>
            </div>
          }
          amountStyle={
            hasChargeback || hasRefund
              ? statusColors.ChargedBack
              : paymentPendingRefund
              ? statusColors.InitialApproval
              : statusColors[processorStatus] || 'none'
          }
        />
      </div>
    </>
  )
}
